/**
 * FontLibrarySection — collapsible table of all fonts with upload / delete /
 * restore / set-default actions. Mounted at the top of ContentPacksPage.
 */

import { useState } from 'react'
import {
  useFontsList,
  useFontDelete,
  useFontUpdate,
  useFontRestore,
} from '../hooks/useFonts'
import { FontUploadModal } from './FontUploadModal'
import { FontPreview } from '@/components/FontPicker'
import type { HttpError } from '@/lib/api/client'
import { toast } from 'sonner'

function errorMessage(err: unknown, fallback: string): string {
  const httpErr = err as HttpError | undefined
  const detail = (httpErr?.responseData as { detail?: unknown } | undefined)?.detail
  if (typeof detail === 'string') return detail
  return httpErr?.message ?? fallback
}

export function FontLibrarySection() {
  const [open, setOpen] = useState(true)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [showDeleted, setShowDeleted] = useState(false)

  const { data: fonts = [], isLoading } = useFontsList(showDeleted)
  const deleteFont = useFontDelete()
  const updateFont = useFontUpdate()
  const restoreFont = useFontRestore()

  const handleSetDefault = (id: number, display_name: string) => {
    updateFont.mutate(
      { id, body: { is_global_default: true } },
      {
        onSuccess: () => toast.success(`已将 "${display_name}" 设为全局默认字体`),
        onError: (err) => toast.error(errorMessage(err, '设置默认字体失败')),
      },
    )
  }

  const handleDelete = (id: number, display_name: string) => {
    if (!confirm(`删除字体 "${display_name}"?`)) return
    deleteFont.mutate(id, {
      onSuccess: () => toast.success(`已删除字体 "${display_name}"`),
      onError: (err) => toast.error(errorMessage(err, '删除失败')),
    })
  }

  const handleRestore = (id: number, display_name: string) => {
    restoreFont.mutate(id, {
      onSuccess: () => toast.success(`已恢复字体 "${display_name}"`),
      onError: (err) => toast.error(errorMessage(err, '恢复失败')),
    })
  }

  return (
    <div id="fonts-section" className="bg-white border border-gray-200 rounded-lg mb-6">
      <div
        className="px-4 py-3 flex items-center justify-between cursor-pointer border-b"
        onClick={() => setOpen(!open)}
      >
        <h2 className="text-base font-semibold text-gray-900">字体库</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setUploadOpen(true)
            }}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            ＋ 上传字体
          </button>
          <span className="text-gray-400">{open ? '▾' : '▸'}</span>
        </div>
      </div>
      {open && (
        <div className="p-4">
          <label className="flex items-center gap-2 text-xs text-gray-600 mb-3">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            显示已删除
          </label>
          {isLoading && <p className="text-sm text-gray-500">加载中...</p>}
          {!isLoading && fonts.length === 0 && (
            <p className="text-sm text-gray-500">还没有字体。点 "上传字体" 添加。</p>
          )}
          {!isLoading && fonts.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-gray-500 border-b">
                <tr>
                  <th className="py-2 w-12">ID</th>
                  <th className="w-32">预览</th>
                  <th>名称</th>
                  <th className="w-20">引用数</th>
                  <th className="w-24">状态</th>
                  <th className="w-40">操作</th>
                </tr>
              </thead>
              <tbody>
                {fonts.map((f) => {
                  const isDeleted = f.deleted_at !== null
                  return (
                    <tr key={f.id} className="border-b last:border-b-0">
                      <td className="py-2">
                        {f.is_global_default && <span className="mr-1">★</span>}
                        {f.id}
                      </td>
                      <td>
                        <FontPreview
                          fontId={f.id}
                          text="幸运"
                          alt={f.display_name}
                          className="h-8"
                        />
                      </td>
                      <td>
                        <div className="font-medium">{f.display_name}</div>
                        <div className="text-xs text-gray-500">
                          {f.name}
                          {f.description ? ` · ${f.description}` : ''}
                        </div>
                      </td>
                      <td>{f.reference_count}</td>
                      <td>
                        {isDeleted ? (
                          <span className="text-red-600 text-xs">已删除</span>
                        ) : f.is_global_default ? (
                          <span className="text-amber-600 text-xs">全局默认</span>
                        ) : (
                          <span className="text-green-600 text-xs">启用</span>
                        )}
                      </td>
                      <td className="space-x-2">
                        {!isDeleted && !f.is_global_default && (
                          <button
                            onClick={() => handleSetDefault(f.id, f.display_name)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            设为默认
                          </button>
                        )}
                        {!isDeleted && !f.is_global_default && (
                          <button
                            onClick={() => handleDelete(f.id, f.display_name)}
                            className="text-xs text-red-600 hover:underline"
                          >
                            删除
                          </button>
                        )}
                        {isDeleted && (
                          <button
                            onClick={() => handleRestore(f.id, f.display_name)}
                            className="text-xs text-green-600 hover:underline"
                          >
                            恢复
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
      <FontUploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </div>
  )
}
